import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dataset } from '@/lib/types';
import { Calendar, Download, Table, TrendUp } from '@phosphor-icons/react';

interface DatasetCardProps {
  dataset: Dataset;
  onSelect: (dataset: Dataset) => void;
  onPreview: (dataset: Dataset) => void;
  isSelected?: boolean;
}

export function DatasetCard({
  dataset,
  onSelect,
  onPreview,
  isSelected,
}: DatasetCardProps) {
  const getQualityColor = (score: number) => {
    if (score >= 95) {
      return 'text-quality-good';
    }
    if (score >= 85) {
      return 'text-quality-warning';
    }

    return 'text-quality-poor';
  };

  const getQualityDot = (score: number) => {
    if (score >= 95) {
      return 'bg-green-500';
    }
    if (score >= 85) {
      return 'bg-orange-500';
    }

    return 'bg-red-500';
  };

  return (
    <Card
      className={`hover:shadow-lg transition-all duration-200 h-[280px] flex flex-col overflow-hidden ${
        isSelected
          ? 'ring-2 ring-primary border-primary'
          : 'border hover:border-primary/20'
      }`}
    >
      <CardHeader className='pb-2 flex-shrink-0'>
        <div className='flex items-start gap-2 min-w-0 overflow-hidden'>
          <Table className='w-4 h-4 text-primary mt-0.5 flex-shrink-0' />
          <div className='flex-1 min-w-0 overflow-hidden'>
            <CardTitle className='text-sm font-semibold leading-tight line-clamp-2 mb-1 break-words'>
              {dataset.name}
            </CardTitle>
            <div className='flex items-center gap-2 min-w-0'>
              <Badge variant='outline' className='text-xs flex-shrink-0'>
                {dataset.category}
              </Badge>
              <div className='flex items-center gap-1 flex-shrink-0'>
                <div
                  className={`w-2 h-2 rounded-full ${getQualityDot(dataset.qualityScore)}`}
                />
                <span className='text-xs font-medium'>
                  {dataset.qualityScore}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className='pt-0 pb-4 flex-1 flex flex-col justify-between min-h-0 overflow-hidden'>
        <div className='space-y-2 min-h-0 overflow-hidden'>
          <CardDescription className='text-xs leading-tight line-clamp-2 min-h-[2.4rem] break-words'>
            {dataset.description}
          </CardDescription>

          <div className='space-y-1.5'>
            <div className='flex items-center justify-between text-xs min-w-0'>
              <span className='text-muted-foreground flex-shrink-0'>Rows:</span>
              <span className='font-mono font-medium truncate ml-2'>
                {dataset.totalRows > 1000000
                  ? `${(dataset.totalRows / 1000000).toFixed(1)}M`
                  : dataset.totalRows > 1000
                    ? `${(dataset.totalRows / 1000).toFixed(1)}K`
                    : dataset.totalRows.toLocaleString()}
              </span>
            </div>

            <div className='flex items-center justify-between text-xs min-w-0'>
              <span className='text-muted-foreground flex-shrink-0'>
                Fields:
              </span>
              <span className='font-mono font-medium'>
                {dataset.schema.length}
              </span>
            </div>

            <div className='flex items-center justify-between text-xs min-w-0'>
              <span className='text-muted-foreground flex-shrink-0'>
                Updated:
              </span>
              <span className='text-muted-foreground truncate ml-2'>
                {new Date(dataset.lastUpdated).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
            </div>
          </div>

          <div className='text-xs overflow-hidden'>
            <div className='text-muted-foreground mb-1'>Key fields:</div>
            <div className='line-clamp-2 min-h-[2.4rem] break-words'>
              {dataset.schema
                .slice(0, 3)
                .map(field => field.name)
                .join(', ')}
              {dataset.schema.length > 3 && ` +${dataset.schema.length - 3}`}
            </div>
          </div>
        </div>

        <div className='flex gap-1.5 mt-auto pt-3 flex-shrink-0'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => onPreview(dataset)}
            className='flex-1 text-xs h-8'
          >
            <TrendUp className='w-3 h-3 mr-1' />
            Preview
          </Button>
          <Button
            onClick={() => onSelect(dataset)}
            size='sm'
            className='flex-1 text-xs h-8'
            variant={isSelected ? 'secondary' : 'default'}
          >
            <Download className='w-3 h-3 mr-1' />
            {isSelected ? 'Selected' : 'Select'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
